import React, { useState, useEffect } from 'react';
import {
  Box, Tabs, Tab, TextField, Button, Typography,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  FormControl, InputLabel, Select, MenuItem, Alert, Snackbar
} from '@mui/material';
import { useAuth } from '../components/AuthContext';
import { API_BASE_URL } from '../components/api.js';

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const initialNewFaltaState = {
  falta_nome: '',
  falta_descricao: ''
};

function Faltas() {
  const [tabIndex, setTabIndex] = useState(0);
  const [faltas, setFaltas] = useState([]);
  const [novoFalta, setNovoFalta] = useState(initialNewFaltaState);
  const [editandoFalta, setEditandoFalta] = useState(null);
  const [selectedFaltaId, setSelectedFaltaId] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const { authenticatedFetch } = useAuth();

  // Buscar todas as faltas
  const fetchFaltas = async () => {
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/api/ponto-falta-editar-faltas-buscar/`);
      if (!response.ok) throw new Error('Erro ao buscar faltas');
      const data = await response.json();
      setFaltas(data);
    } catch (err) {
      console.error(err);
      setFaltas([]);
    }
  };

  useEffect(() => {
    fetchFaltas();
  }, []);

  const handleSnackbar = (message, severity) => setSnackbar({ open: true, message, severity });

  // Adicionar nova falta
  const handleRegisterChange = (e) => {
    const { name, value } = e.target;
    setNovoFalta(prev => ({ ...prev, [name]: value }));
  };

  const handleRegisterSubmit = async () => {
    try {
      await authenticatedFetch(`${API_BASE_URL}/api/ponto-falta-adicionar/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(novoFalta)
      });
      handleSnackbar(`Falta '${novoFalta.falta_nome}' registrada com sucesso!`, 'success');
      setNovoFalta(initialNewFaltaState);
      fetchFaltas();
    } catch (err) {
      console.error(err);
      handleSnackbar('Erro ao registrar falta.', 'error');
    }
  };

  // Editar falta
  const handleSelectChange = (e) => {
    const val = e.target.value;
    setSelectedFaltaId(val);
    const falta = faltas.find(f => f.falta_id === val) || null;
    setEditandoFalta(falta ? { ...falta } : null);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditandoFalta(prev => ({ ...prev, [name]: value }));
  };

  const handleEditSubmit = async () => {
    if (!editandoFalta) return;
    try {
      await authenticatedFetch(`${API_BASE_URL}/api/ponto-falta-editar/${editandoFalta.falta_id}/`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          falta_nome: editandoFalta.falta_nome,
          falta_descricao: editandoFalta.falta_descricao
        })
      });
      handleSnackbar('Alterações salvas com sucesso!', 'success');
      fetchFaltas();
    } catch (err) {
      console.error(err);
      handleSnackbar('Erro ao salvar alterações.', 'error');
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Tabs value={tabIndex} onChange={(e, newValue) => setTabIndex(newValue)}>
        <Tab label="Adicionar Falta" />
        <Tab label="Editar Falta" />
      </Tabs>

      {/* Adicionar Falta */}
      <TabPanel value={tabIndex} index={0}>
        <Typography variant="h6" sx={{ mb: 2 }}>Adicionar nova Falta</Typography>
        <Box sx={{ width: 400, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Nome da Falta"
            name="falta_nome"
            value={novoFalta.falta_nome}
            onChange={handleRegisterChange}
            fullWidth
          />
          <TextField
            label="Descrição"
            name="falta_descricao"
            value={novoFalta.falta_descricao}
            onChange={handleRegisterChange}
            multiline
            rows={10}
            fullWidth
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="contained" onClick={handleRegisterSubmit}>Salvar</Button>
            <Button variant="outlined" onClick={() => setNovoFalta(initialNewFaltaState)}>Cancelar</Button>
          </Box>
        </Box>
      </TabPanel>

      {/* Editar Falta */}
      <TabPanel value={tabIndex} index={1}>
        <Typography variant="h6" sx={{ mb: 2 }}>Editar Falta</Typography>

        <FormControl sx={{ minWidth: 300, mb: 2 }}>
          <InputLabel id="edit-falta-label">Selecione uma Falta</InputLabel>
          <Select
            labelId="edit-falta-label"
            label="Selecione uma Falta"
            value={selectedFaltaId}
            onChange={handleSelectChange}
          >
            <MenuItem value=""><em>Nenhuma</em></MenuItem>
            {faltas.map(f => (
              <MenuItem key={f.falta_id} value={f.falta_id}>{f.falta_nome}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {editandoFalta ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="ID"
              value={editandoFalta.falta_id}
              disabled
              fullWidth
            />
            <TextField
              label="Nome da Falta"
              name="falta_nome"
              value={editandoFalta.falta_nome}
              onChange={handleEditChange}
              fullWidth
            />
            <TextField
              label="Descrição"
              name="falta_descricao"
              value={editandoFalta.falta_descricao}
              onChange={handleEditChange}
              multiline
              rows={4}
              fullWidth
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button variant="contained" onClick={handleEditSubmit}>Salvar Alterações</Button>
            </Box>
          </Box>
        ) : (
          <Alert severity="info">Selecione uma falta para editar.</Alert>
        )}
      </TabPanel>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Faltas;